import type { PublicationModel } from "../../types/models";
import { isImmutablePub } from "./engine/status";
import { buildSchedulingBatchRequest, type SchedulingBatchRequest } from "./schedulingBatch";
import type { PublicationUpdateCandidate } from "./types";

/** Capture only the rows the user saw and chose to change. */
export function captureQuickActionBaseline(
  publications: readonly PublicationModel[] | undefined,
  targets: readonly PublicationModel[],
): PublicationModel[] {
  if (!publications || targets.length === 0 || targets.some(p =>
    isImmutablePub(p) || !["scheduled", "failed"].includes(p.status) ||
    !publications.some(row => row.id === p.id && row.projectId === p.projectId))) {
    throw new Error("The schedule changed. Reload before trying again.");
  }
  return structuredClone([...targets]);
}

export function buildQuickActionRequest({ baseline, updates = [], deletes = [] }: {
  baseline: readonly PublicationModel[];
  updates?: readonly PublicationUpdateCandidate[];
  deletes?: readonly PublicationModel[];
}): SchedulingBatchRequest {
  return buildSchedulingBatchRequest({
    baselinePublications: baseline,
    creates: [], updates, deletes,
  });
}

/** Prevent overlapping clicks; each confirmed action is a fresh submission. */
export function createQuickActionWriter(write: (request: SchedulingBatchRequest) => Promise<void>) {
  let saving = false;
  return {
    async run(createRequest: () => SchedulingBatchRequest) {
      if (saving) throw new Error("A publication change is still saving. Try again when it finishes.");
      saving = true;
      try {
        await write(createRequest());
      } finally {
        saving = false;
      }
    },
  };
}
